import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'

import {
  ConnectionIcon,
  PagedTable,
  PagedTableFunctions,
  SearchInput,
} from 'app/common/presentation'
import { CloseIcon, DropDownIcon, MoreIcon, PlusIcon } from 'app/common/presentation/icons'
import { useObjectives } from 'app/objectives/application'
import { useSignalModels, useSignals } from 'app/signals/application'
import { Signal, SignalPlatformName, SignalStatus } from 'app/signals/types'

import {
  Button,
  Checkbox,
  Chip,
  CodeTag,
  DropDown,
  IconButton,
  PageBanner,
  Table,
} from 'components'
import type { ChipColor } from 'components/Chip'

import styles from './Signals.module.scss'

const statuses: SignalStatus[] = ['active', 'archived', 'preparation', 'paused', 'new']

const statusColors: Record<SignalStatus, ChipColor> = {
  active: 'green',
  archived: 'neutral',
  new: 'purple',
  paused: 'neutral',
  preparation: 'yellow',
}

const getSignalPurpose = (signal: Signal) => {
  if (signal.type === 'conversion') return signal.config.purpose
  return signal.config.isValueBased ? 'valueBased' : signal.config.purpose
}

const toggleValue = <T,>(values: T[], value: T) =>
  values.includes(value) ? values.filter((entry) => entry !== value) : [...values, value]

const Signals = () => {
  const navigate = useNavigate()
  const { t } = useTranslation('signals')
  const { signals } = useSignals()
  const models = useSignalModels(signals)
  const { objectives } = useObjectives()
  const [search, setSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [selectedPlatforms, setSelectedPlatforms] = useState<SignalPlatformName[]>([])
  const [selectedStatuses, setSelectedStatuses] = useState<SignalStatus[]>([])
  const pagedTableRef = useRef<PagedTableFunctions>(null)

  const objectiveNames = useMemo(
    () => new Map(objectives.map((objective) => [objective.id, objective.name])),
    [objectives],
  )
  const platforms = useMemo(
    () => [...new Set(signals.map((signal) => signal.connection.platform.name))].sort(),
    [signals],
  )

  const filteredSignals = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase()

    return signals.filter((signal) => {
      if (selectedStatuses.length && !selectedStatuses.includes(signal.status)) return false
      if (
        selectedPlatforms.length &&
        !selectedPlatforms.includes(signal.connection.platform.name)
      ) {
        return false
      }
      if (!normalizedSearch) return true

      return [
        signal.name,
        signal.type,
        signal.config.purpose,
        signal.connection.platform.name,
        objectiveNames.get(signal.objective),
        signal.model ? models.get(signal.model)?.type : undefined,
        signal.status,
      ].some((value) => value?.toLocaleLowerCase().includes(normalizedSearch))
    })
  }, [models, objectiveNames, search, selectedPlatforms, selectedStatuses, signals])

  useEffect(() => {
    pagedTableRef.current?.setPage(1)
  }, [search, selectedPlatforms, selectedStatuses])

  const allSelected =
    filteredSignals.length > 0 && filteredSignals.every(({ id }) => selectedIds.includes(id))
  const someSelected = filteredSignals.some(({ id }) => selectedIds.includes(id))

  const toggleAll = () => {
    const filteredIds = filteredSignals.map(({ id }) => id)
    setSelectedIds((current) =>
      allSelected
        ? current.filter((id) => !filteredIds.includes(id))
        : [...new Set([...current, ...filteredIds])],
    )
  }

  return (
    <div className={styles.signals}>
      <PageBanner subtitle={t('description')} title={t('title')}>
        <Button onClick={() => void navigate('/signals/new')} rightIcon={<PlusIcon />}>
          {t('actions.newSignal')}
        </Button>
      </PageBanner>

      <div className={styles.toolbar}>
        <SearchInput
          className={styles.search}
          onChange={setSearch}
          placeholder={t('overview.search')}
          value={search}
        />
        <div className={styles.filters}>
          <DropDown<SignalStatus>
            data={statuses}
            onReset={() => setSelectedStatuses([])}
            onSelect={(status) => setSelectedStatuses((current) => toggleValue(current, status))}
            placeholder={t('overview.filters.status')}
            selection={selectedStatuses}
            type="multiple"
          >
            <Button rightIcon={<DropDownIcon />} small variant="tertiary">
              {t('overview.filters.status')}
            </Button>
          </DropDown>
          <DropDown<SignalPlatformName>
            data={platforms}
            onReset={() => setSelectedPlatforms([])}
            onSelect={(platform) =>
              setSelectedPlatforms((current) => toggleValue(current, platform))
            }
            placeholder={t('overview.filters.platform')}
            selection={selectedPlatforms}
            type="multiple"
          >
            <Button rightIcon={<DropDownIcon />} small variant="tertiary">
              {t('overview.filters.platform')}
            </Button>
          </DropDown>
        </div>
      </div>

      <div className={styles.tableContainer}>
        {(selectedStatuses.length > 0 || selectedPlatforms.length > 0) && (
          <div className={styles.appliedFilters}>
            {selectedStatuses.length > 0 && (
              <button className={styles.filterChip} onClick={() => setSelectedStatuses([])}>
                <span>{t('overview.filters.status')}</span>
                <strong>
                  {selectedStatuses.map((status) => t(`status.${status}`)).join(', ')}
                </strong>
                <CloseIcon />
              </button>
            )}
            {selectedPlatforms.length > 0 && (
              <button className={styles.filterChip} onClick={() => setSelectedPlatforms([])}>
                <span>{t('overview.filters.platform')}</span>
                <strong>
                  {selectedPlatforms.map((platform) => t(`platform.${platform}`)).join(', ')}
                </strong>
                <CloseIcon />
              </button>
            )}
            <button
              className={styles.clearFilters}
              onClick={() => {
                setSelectedPlatforms([])
                setSelectedStatuses([])
              }}
            >
              {t('overview.filters.clearAll')}
            </button>
          </div>
        )}

        <PagedTable
          ref={pagedTableRef}
          className={styles.table}
          columns={[
            <Checkbox
              checked={allSelected}
              indeterminate={!allSelected && someSelected}
              key="select"
              onClick={toggleAll}
            />,
            t('overview.table.name'),
            t('overview.table.spend'),
            t('overview.table.type'),
            t('overview.table.platform'),
            t('overview.table.objective'),
            t('overview.table.model'),
            t('overview.table.status'),
            '',
          ]}
          data={filteredSignals}
          renderRow={(signal) => {
            const selected = selectedIds.includes(signal.id)

            return (
              <Table.Row className={selected ? styles.selectedRow : undefined} key={signal.id}>
                <Table.Cell className={styles.checkboxCell}>
                  <Checkbox
                    checked={selected}
                    onClick={() => setSelectedIds((current) => toggleValue(current, signal.id))}
                  />
                </Table.Cell>
                <Table.Cell className={styles.nameCell}>
                  <Link to={`/signals/${signal.id}`}>{signal.name}</Link>
                </Table.Cell>
                <Table.Cell>{null}</Table.Cell>
                <Table.Cell>
                  {t(`overview.type.${signal.type}`)} ·{' '}
                  {t(`overview.purpose.${getSignalPurpose(signal)}`)}
                </Table.Cell>
                <Table.Cell className={styles.platformCell}>
                  <ConnectionIcon
                    connection={{ name: signal.connection.platform.name }}
                    style={{ height: 16, width: 16 }}
                  />
                  {t(`platform.${signal.connection.platform.name}`)}
                </Table.Cell>
                <Table.Cell>{objectiveNames.get(signal.objective) ?? '-'}</Table.Cell>
                <Table.Cell>
                  <CodeTag className={styles.modelPath} color="grey">
                    <span title={signal.model ? models.get(signal.model)?.path : undefined}>
                      {(signal.model ? models.get(signal.model)?.type : undefined) ?? '-'}
                    </span>
                  </CodeTag>
                </Table.Cell>
                <Table.Cell>
                  <Chip color={statusColors[signal.status]}>{t(`status.${signal.status}`)}</Chip>
                </Table.Cell>
                <Table.Cell className={styles.actionsCell}>
                  <IconButton
                    aria-label={t('overview.actions.openMenu', { name: signal.name })}
                    icon={MoreIcon}
                    small
                    variant="tertiary"
                  />
                </Table.Cell>
              </Table.Row>
            )
          }}
          useSearchParamsPaging
        />
      </div>
    </div>
  )
}

export default Signals
